import {
  Plus,
  FolderOpen,
  Upload,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  FileText,
  X,
} from "lucide-react";
import { useState, useEffect, useRef, ChangeEvent } from "react";
import { integrationService } from "../services/IntegrationService";
import { StatusModal } from "./StatusModal";

// The wiped causit-entities bucket model — an uploaded file is the tenant's
// BLOB primitive crossing to world; there is no outlet bucket list anymore
// (IntegrationService.getBuckets died with the surface cut). This view keeps
// only the upload affordance; the bucket list renders empty.
export interface FileBucketSummary {
  bucketId: string;
  bucketName: string;
  createdAt: string;
  fileCount: number;
  files?: {
    fileId: string;
    fileName: string;
    fileSize: number;
    uploadedAt: string;
    s3Key: string;
  }[];
}

interface FileBucketsProps {
  isOpen: boolean;
  isModal?: boolean;
  onClose?: () => void;
  surface?: "card" | "section";
  showHeader?: boolean;
  uploadTriggerKey?: number;
  refreshKey?: number;
  hiddenUploadOnly?: boolean;
  readOnly?: boolean;
  onChanged?: () => void;
}

type PendingDateUpload = {
  file: File;
  bucketId?: string;
  bucketName?: string;
  inputKey?: string;
  providedTimestamp: string;
  isSubmitting: boolean;
  error: string | null;
};

const formatDate = (value?: string) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString();
};

export const FileBuckets = ({
  isOpen,
  isModal = false,
  onClose,
  surface = "card",
  showHeader = true,
  uploadTriggerKey,
  refreshKey,
  hiddenUploadOnly = false,
  readOnly = false,
  onChanged,
}: FileBucketsProps) => {
  const [buckets, setBuckets] = useState<FileBucketSummary[]>([]);
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(
    new Set()
  );
  const [uploadingBuckets, setUploadingBuckets] = useState<Set<string>>(
    new Set()
  );

  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "waiting";
    message: string;
  } | null>(null);

  const [isCreatingBucket, setIsCreatingBucket] = useState(false);

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const newBucketFileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingDateUpload, setPendingDateUpload] =
    useState<PendingDateUpload | null>(null);

  const loadBuckets = async () => {
    // causit-entities is WIPED — uploaded files are world blob primitives;
    // there is no outlet bucket list to fetch (the surface cut killed the
    // dead getBuckets round trip). The list renders empty; upload stays.
    setError(null);
    setBuckets([]);
    setIsLoadingBuckets(false);
  };

  useEffect(() => {
    if (isOpen && !hiddenUploadOnly) {
      loadBuckets();
    }
  }, [isOpen, hiddenUploadOnly, refreshKey]);

  useEffect(() => {
    if (!isOpen || uploadTriggerKey === undefined || uploadTriggerKey === 0) {
      return;
    }
    newBucketFileInputRef.current?.click();
  }, [isOpen, uploadTriggerKey]);

  const handleNewBucketFileSelect = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setStatusMessage({
        type: "error",
        message: "Only CSV files are supported",
      });
      setTimeout(() => setStatusMessage(null), 2000);
      event.target.value = "";
      return;
    }
    await startCsvUpload(file, {});
  };

  const uploadCsvToBucket = async (
    file: File,
    bucketId?: string,
    bucketName?: string,
    providedTimestamp?: string
  ) => {
    const uploadResponse = await integrationService.uploadAttachment(file, {
      bucketId,
      bucketName,
      providedTimestamp,
    });


    return uploadResponse;
  };

  const csvAppearsTimestamped = async (file: File): Promise<boolean> => {
    const text = await file.slice(0, 65536).text();
    const lines = text
      .split(/\r?\n/)
      .slice(0, 25)
      .map((line) => line.trim())
      .filter(Boolean);
    const haystack = lines.join("\n").toLowerCase();
    if (/\b(date|timestamp|time|created_at|updated_at|created_date)\b/.test(haystack)) {
      return true;
    }
    return /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/.test(haystack);
  };

  const parseTimestampToIso = (
    raw: string
  ): { iso: string | null; error: string | null } => {
    const value = raw.trim();
    if (!value) return { iso: null, error: null };

    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      const dt = new Date(
        Date.UTC(Number(y), Number(m) - 1, Number(d), 23, 59, 59, 999)
      );
      if (!Number.isNaN(dt.getTime())) return { iso: dt.toISOString(), error: null };
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return { iso: null, error: "Could not parse timestamp." };
    }
    return { iso: parsed.toISOString(), error: null };
  };

  const startCsvUpload = async (
    file: File,
    opts: { bucketId?: string; bucketName?: string; inputKey?: string }
  ) => {
    if (await csvAppearsTimestamped(file)) {
      if (opts.bucketId) {
        await uploadIntoExistingBucket(file, opts.bucketId, opts.bucketName);
      } else {
        await createBucketAndUpload(file);
      }
      return;
    }

    setPendingDateUpload({
      file,
      bucketId: opts.bucketId,
      bucketName: opts.bucketName,
      inputKey: opts.inputKey,
      providedTimestamp: "",
      isSubmitting: false,
      error: null,
    });
  };

  const createBucketAndUpload = async (
    file: File,
    providedTimestamp?: string
  ) => {
    try {
      setIsCreatingBucket(true);
      setStatusMessage({
        type: "waiting",
        message: "Creating bucket and uploading file...",
      });

      const uploadResponse = await uploadCsvToBucket(
        file,
        undefined,
        undefined,
        providedTimestamp
      );
      await loadBuckets();

      setStatusMessage({
        type: "success",
        message: uploadResponse.message || "Bucket created and file uploaded successfully",
      });
      onChanged?.();

      setTimeout(() => setStatusMessage(null), 2000);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error || err?.message || "Failed to create bucket";

      setStatusMessage({
        type: "error",
        message: errorMessage,
      });

      setTimeout(() => setStatusMessage(null), 2000);
    } finally {
      setIsCreatingBucket(false);
    }
  };

  const uploadIntoExistingBucket = async (
    file: File,
    bucketId: string,
    bucketName?: string,
    providedTimestamp?: string
  ) => {
    try {
      setUploadingBuckets((prev) => new Set(prev).add(bucketId));
      setStatusMessage({
        type: "waiting",
        message: "Uploading file...",
      });

      const uploadResponse = await uploadCsvToBucket(
        file,
        bucketId,
        bucketName || buckets.find((b) => b.bucketId === bucketId)?.bucketName,
        providedTimestamp
      );

      await loadBuckets();

      setStatusMessage({
        type: "success",
        message: uploadResponse.message || "File uploaded successfully",
      });
      onChanged?.();

      setTimeout(() => setStatusMessage(null), 2000);
    } catch (err: any) {

      const errorMessage =
        err?.response?.data?.error || err?.message || "Failed to upload file";

      setStatusMessage({
        type: "error",
        message: errorMessage,
      });

      setTimeout(() => setStatusMessage(null), 2000);
    } finally {
      setUploadingBuckets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bucketId);
        return newSet;
      });

      // Reset file input
      if (fileInputRefs.current[bucketId]) {
        fileInputRefs.current[bucketId]!.value = "";
      }
    }
  };

  const handleFileSelect = async (
    bucketId: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setStatusMessage({
        type: "error",
        message: "Only CSV files are supported",
      });
      setTimeout(() => setStatusMessage(null), 2000);
      return;
    }

    await startCsvUpload(file, {
      bucketId,
      bucketName: buckets.find((b) => b.bucketId === bucketId)?.bucketName,
      inputKey: bucketId,
    });
  };

  const completePendingDateUpload = async (providedTimestamp?: string) => {
    if (!pendingDateUpload) return;
    setPendingDateUpload((prev) =>
      prev ? { ...prev, isSubmitting: true, error: null } : prev
    );
    try {
      if (pendingDateUpload.bucketId) {
        await uploadIntoExistingBucket(
          pendingDateUpload.file,
          pendingDateUpload.bucketId,
          pendingDateUpload.bucketName,
          providedTimestamp
        );
      } else {
        await createBucketAndUpload(pendingDateUpload.file, providedTimestamp);
      }
      setPendingDateUpload(null);
      if (pendingDateUpload.inputKey && fileInputRefs.current[pendingDateUpload.inputKey]) {
        fileInputRefs.current[pendingDateUpload.inputKey]!.value = "";
      }
      if (newBucketFileInputRef.current) newBucketFileInputRef.current.value = "";
    } catch (err: any) {
      setPendingDateUpload((prev) =>
        prev
          ? {
              ...prev,
              isSubmitting: false,
              error: err?.message || "Failed to upload file",
            }
          : prev
      );
    }
  };

  const toggleBucketExpansion = (bucketId: string) => {
    setExpandedBuckets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(bucketId)) {
        newSet.delete(bucketId);
      } else {
        newSet.add(bucketId);
      }
      return newSet;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  if (!isOpen) return null;

  const content = (
    <div className={isModal || surface === "section" ? "space-y-4" : "app-card p-8"}>
      {/* Header */}
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2
              className={`font-semibold text-[var(--text-primary)] ${
                isModal ? "text-xl" : "text-2xl"
              } mb-2`}
            >
              File Buckets
            </h2>
            <p className="text-[var(--text-secondary)]">
              Manage your CSV files for data analysis
            </p>
          </div>
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 hover:bg-[var(--accent-blue)]/10 border hover:border-[var(--accent-blue)]/50 text-[var(--text-primary)] hover:text-[var(--accent-blue)] rounded-lg transition-all duration-200 active:scale-95 w-full sm:w-auto"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
            onClick={() => newBucketFileInputRef.current?.click()}
            disabled={isCreatingBucket}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Upload CSV to new bucket</span>
          </button>
        </div>
      )}
      <input
        type="file"
        ref={newBucketFileInputRef}
        onChange={handleNewBucketFileSelect}
        accept=".csv"
        className="hidden"
      />

      {/* Buckets List */}
      {!hiddenUploadOnly && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
        {buckets.map((bucket) => (
          <div
            key={bucket.bucketId}
            className="p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-primary)] min-w-0"
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="w-11 h-11 bg-[var(--accent-blue)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-5 h-5 text-white" />
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[bucket.bucketId] = el)}
                    onChange={(e) => handleFileSelect(bucket.bucketId, e)}
                    accept=".csv"
                    className="hidden"
                  />
                  {!readOnly && (
                  <button
                    className="p-2 text-[var(--accent-blue)] hover:opacity-80 hover:bg-[var(--accent-blue)]/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() =>
                      fileInputRefs.current[bucket.bucketId]?.click()
                    }
                    disabled={uploadingBuckets.has(bucket.bucketId)}
                    title="Upload CSV file"
                  >
                    {uploadingBuckets.has(bucket.bucketId) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </button>
                  )}
                  {bucket.fileCount > 0 && (
                    <button
                      className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                      onClick={() => toggleBucketExpansion(bucket.bucketId)}
                      title={
                        expandedBuckets.has(bucket.bucketId)
                          ? "Hide files"
                          : "Show files"
                      }
                    >
                      {expandedBuckets.has(bucket.bucketId) ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="min-w-0 space-y-2">
                <h3 className="text-base font-semibold text-[var(--text-primary)] truncate">
                  {bucket.bucketName}
                </h3>
                <p className="text-[var(--accent-blue)] text-sm font-medium">
                  {bucket.fileCount} file{bucket.fileCount !== 1 ? "s" : ""}
                </p>
                <p className="text-sm text-[var(--text-muted)]">
                  Created {formatDate(bucket.createdAt)}
                </p>
              </div>

              {expandedBuckets.has(bucket.bucketId) && bucket.files && (
                <div className="pt-3 border-t border-[var(--border-primary)] space-y-2">
                  {bucket.files.length === 0 ? (
                    <p className="text-[var(--text-muted)] text-sm">
                      No files in this bucket
                    </p>
                  ) : (
                    bucket.files.map((file) => (
                      <div
                        key={file.fileId}
                        className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg"
                      >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <FileText className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[var(--text-primary)] text-sm truncate">
                              {file.fileName}
                            </p>
                            <p className="text-[var(--text-muted)] text-xs">
                              {formatFileSize(file.fileSize)} •{" "}
                              {formatDate(file.uploadedAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoadingBuckets && (
          <div className="text-center py-12 text-[var(--text-muted)] min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] animate-spin" />
            <p className="text-lg mb-2">Loading buckets...</p>
          </div>
        )}

        {error && !isLoadingBuckets && (
          <div className="text-center py-12 text-[var(--text-muted)] min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <p className="text-lg mb-2 text-red-400">Error loading buckets</p>
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={loadBuckets}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoadingBuckets && !error && buckets.length === 0 && (
          <div className="text-center py-12 text-[var(--text-muted)] min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)]" />
            <p className="text-lg mb-2">No buckets created yet</p>
            <p className="text-sm">
              Create a bucket to start uploading CSV files
            </p>
          </div>
        )}
      </div>
      )}

      {/* Status Modal */}
      {statusMessage && (
        <StatusModal
          isOpen={!!statusMessage}
          type={statusMessage.type}
          message={statusMessage.message}
          onClose={() => setStatusMessage(null)}
          autoHideDuration={statusMessage.type === "success" ? 3000 : 5000}
        />
      )}

      {pendingDateUpload && (
        <div className="fixed inset-0 bg-[var(--modal-backdrop)] backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="bg-[var(--modal-bg)] rounded-xl w-full max-w-lg p-6 shadow-2xl border border-[var(--border-primary)]">
            <div className="mb-4">
              <h3 className="text-[var(--text-primary)] text-lg font-semibold">
                Set file date
              </h3>
              <p className="text-[var(--text-muted)] text-sm mt-1">
                {pendingDateUpload.file.name}
              </p>
            </div>

            {pendingDateUpload.error && (
              <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-sm">
                {pendingDateUpload.error}
              </div>
            )}

            <input
              value={pendingDateUpload.providedTimestamp}
              onChange={(event) =>
                setPendingDateUpload((prev) =>
                  prev
                    ? {
                        ...prev,
                        providedTimestamp: event.target.value,
                        error: null,
                      }
                    : prev
                )
              }
              placeholder="YYYY-MM-DD"
              className="w-full bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-lg px-3 py-2 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-blue)]/60"
              disabled={pendingDateUpload.isSubmitting}
            />

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={pendingDateUpload.isSubmitting}
                onClick={() => completePendingDateUpload()}
              >
                Skip
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-[var(--accent-blue)] hover:opacity-90 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={pendingDateUpload.isSubmitting}
                onClick={() => {
                  const parsed = parseTimestampToIso(
                    pendingDateUpload.providedTimestamp
                  );
                  if (!parsed.iso) {
                    setPendingDateUpload((prev) =>
                      prev
                        ? {
                            ...prev,
                            error:
                              parsed.error ||
                              "Please enter a parseable timestamp.",
                          }
                        : prev
                    );
                    return;
                  }
                  completePendingDateUpload(parsed.iso);
                }}
              >
                {pendingDateUpload.isSubmitting && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );

  // If modal mode, wrap in modal UI
  if (isModal) {
    return (
      <>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--modal-backdrop)] backdrop-blur-sm">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[var(--modal-bg)] rounded-xl border border-[var(--border-primary)] shadow-2xl">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-all duration-200"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-6 sm:p-8">{content}</div>
          </div>
        </div>
      </>
    );
  }

  // Otherwise render embedded
  return content;
};
